import React, { useState, useEffect } from "react";
import { closeModal } from "../../store/modal/modalReducer";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faSearch } from "@fortawesome/pro-regular-svg-icons";
import * as ProRegularIcons from "@fortawesome/pro-regular-svg-icons";
import * as ProSolidIcons from "@fortawesome/pro-solid-svg-icons";
import { Dialog, Transition } from "@headlessui/react";
import NotificationToast from "../Helpers/NotificationToast";
import { getBooking } from "../../store/booking/bookingActions";

export default function ModalChatTag(props) {
    const dispatch = useDispatch();
    const [open] = useState(true);
    const { chatTags } = useSelector((state) => state.chatTags);
    const { current_staff, staff } = useSelector((state) => state.auth);
    const { current_booking } = useSelector((state) => state.booking);
    const [selectedTags, setSelectedTags] = useState([]);
    const [currentTags, setCurrentTags] = useState([]);
    const [assignedStaff, setAssignedStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        if (props.bookingId) {
            fetchCurrentTags();
            fetchAssignedStaff();
        }
    }, [props.bookingId]);

    const fetchCurrentTags = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}bookings/${props.bookingId}/chat-tags`
            );
            const tagIds = (response.data || []).map((tag) => tag.id);
            setCurrentTags(tagIds);
            setSelectedTags(tagIds);
        } catch (error) {
            console.error("Error fetching current tags:", error);
            setCurrentTags([]);
            setSelectedTags([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchAssignedStaff = () => {
        if (current_booking?.assigned_to) {
            // Handle both string and number types
            const assignedToStr = String(current_booking.assigned_to);
            if (assignedToStr === '0' || assignedToStr === '') {
                setAssignedStaff([]);
                return;
            }
            const staffIds = assignedToStr.split(',').map(id => parseInt(id.trim())).filter(id => id > 0);
            const staffMembers = staffIds.map(id => staff.find(s => s.id === id)).filter(Boolean);
            setAssignedStaff(staffMembers);
        } else {
            setAssignedStaff([]);
        }
    };

    const handleAddTag = async (tagId) => {
        try {
            await axios.post(
                `${process.env.REACT_APP_API_URL}bookings/${props.bookingId}/chat-tags`,
                {
                    chat_tag_id: tagId,
                    added_by: current_staff?.id,
                }
            );
            setSelectedTags((prev) => [...prev, tagId]);
            toast(<NotificationToast title="Success" message="Tag added" />);
            dispatch(getBooking());
        } catch (error) {
            console.error("Error adding tag:", error);
            toast(<NotificationToast title="Error" message="Failed to add tag" type="error" />);
        }
    };

    const handleRemoveTag = async (tagId) => {
        try {
            await axios.delete(
                `${process.env.REACT_APP_API_URL}bookings/${props.bookingId}/chat-tags/${tagId}`,
                {
                    data: { removed_by: current_staff?.id },
                }
            );
            setSelectedTags((prev) => prev.filter((id) => id !== tagId));
            toast(<NotificationToast title="Success" message="Tag removed" />);
            dispatch(getBooking());
        } catch (error) {
            console.error("Error removing tag:", error);
            toast(<NotificationToast title="Error" message="Failed to remove tag" type="error" />);
        }
    };

    const handleAddStaff = async (staffId) => {
        try {
            const staffMember = staff.find(s => s.id === staffId);
            if (!staffMember) return;
            
            // Create a note with @mention to trigger staff assignment
            const formData = new FormData();
            formData.append('message', `<a href="${staffId}" class="wysiwyg-mention" data-staff-id="${staffId}" data-mention-type="staff">@${staffMember.name}</a> added to booking`);
            formData.append('platform', 'note');
            formData.append('booking', props.bookingId);
            formData.append('from', current_staff?.id);
            formData.append('to', 0);
            formData.append('user_id', current_booking?.user?.id || 0);
            
            await axios.post(`${process.env.REACT_APP_API_URL}send`, formData);
            
            setAssignedStaff(prev => [...prev, staffMember]);
            
            toast(<NotificationToast title="Success" message="Staff member added" />);
            dispatch(getBooking());
        } catch (error) {
            console.error("Error adding staff:", error);
            toast(<NotificationToast title="Error" message="Failed to add staff member" type="error" />);
        }
    };

    const handleRemoveStaff = async (staffId) => {
        try {
            await axios.post(`${process.env.REACT_APP_API_URL}removemention/${props.bookingId}`, {
                staff_id: staffId
            });
            
            setAssignedStaff(prev => prev.filter(s => s.id !== staffId));
            
            toast(<NotificationToast title="Success" message="Staff member removed" />);
            dispatch(getBooking());
        } catch (error) {
            console.error("Error removing staff:", error);
            toast(<NotificationToast title="Error" message="Failed to remove staff member" type="error" />);
        }
    };

    const getIconComponent = (iconName) => {
        if (!iconName) return null;
        
        // Convert icon name to camelCase format (e.g., "wrench" -> "faWrench")
        const camelCaseIcon = 'fa' + iconName.charAt(0).toUpperCase() + iconName.slice(1).replace(/-([a-z])/g, (g) => g[1].toUpperCase());
        
        // Try to find icon in pro-regular first, then pro-solid
        const icon = ProRegularIcons[camelCaseIcon] || ProSolidIcons[camelCaseIcon];
        return icon;
    };

    // Get assigned and available items
    const assignedTags = chatTags.filter((tag) => selectedTags.includes(tag.id));
    
    // Filter available items by search query
    const availableTags = chatTags.filter((tag) => {
        if (!tag.active || selectedTags.includes(tag.id)) return false;
        if (!searchQuery) return true;
        return tag.name.toLowerCase().includes(searchQuery.toLowerCase());
    });
    
    const availableStaff = staff.filter((s) => {
        if (assignedStaff.find(as => as.id === s.id) || s.status === 'Applicant') return false;
        if (!searchQuery) return true;
        return s.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
               (s.email && s.email.toLowerCase().includes(searchQuery.toLowerCase()));
    });

    return (
        <Transition.Root show={open} as={React.Fragment}>
            <Dialog
                as="div"
                static
                className="fixed z-50 inset-0 overflow-y-auto"
                open={open}
                onClose={() => dispatch(closeModal())}
            >
                <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                    <Transition.Child
                        as={React.Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
                        &#8203;
                    </span>

                    <Transition.Child
                        as={React.Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-auto shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                            <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                                <button
                                    type="button"
                                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-0"
                                    onClick={() => dispatch(closeModal())}
                                >
                                    <span className="sr-only">Close</span>
                                    <FontAwesomeIcon className="h-6 w-6" aria-hidden="true" icon={faTimes} />
                                </button>
                            </div>

                            <div className="pr-8 w-full max-h-[80vh] overflow-y-auto">
                                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                    <Dialog.Title as="h1" className="text-lg font-medium text-gray-900 mb-4">
                                        Manage Tags & Staff
                                    </Dialog.Title>

                                    {loading ? (
                                        <div className="py-4 text-center text-gray-500">Loading...</div>
                                    ) : (
                                        <>
                                            {/* Currently Assigned Section */}
                                            {(assignedTags.length > 0 || assignedStaff.length > 0) && (
                                                <div className="mb-6">
                                                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Currently Assigned</h3>
                                                    <div className="space-y-2">
                                                        {/* Assigned Tags */}
                                                        {assignedTags.map((tag) => {
                                                            const icon = getIconComponent(tag.icon);
                                                            return (
                                                                <div
                                                                    key={`assigned-tag-${tag.id}`}
                                                                    className="flex items-center justify-between p-3 border border-blue-200 rounded-md bg-blue-50"
                                                                >
                                                                    <div className="flex items-center">
                                                                        <div className="h-8 w-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-medium mr-3">
                                                                            {icon ? (
                                                                                <FontAwesomeIcon icon={icon} className="h-4 w-4" />
                                                                            ) : (
                                                                                tag.name.substring(0, 2).toUpperCase()
                                                                            )}
                                                                        </div>
                                                                        <span className="text-sm font-medium text-gray-900">{tag.name}</span>
                                                                        <span className="ml-2 text-xs text-gray-500">(Tag)</span>
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleRemoveTag(tag.id)}
                                                                        className="px-3 py-1 text-xs font-medium text-red-600 hover:text-red-800 hover:bg-red-100 rounded"
                                                                    >
                                                                        Remove
                                                                    </button>
                                                                </div>
                                                            );
                                                        })}
                                                        
                                                        {/* Assigned Staff */}
                                                        {assignedStaff.map((staffMember) => (
                                                            <div
                                                                key={`assigned-staff-${staffMember.id}`}
                                                                className="flex items-center justify-between p-3 border border-green-200 rounded-md bg-green-50"
                                                            >
                                                                <div className="flex items-center">
                                                                    <div className="h-8 w-8 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-medium mr-3">
                                                                        {staffMember.name.substring(0, 2).toUpperCase()}
                                                                    </div>
                                                                    <span className="text-sm font-medium text-gray-900">{staffMember.name}</span>
                                                                    <span className="ml-2 text-xs text-gray-500">(Staff)</span>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleRemoveStaff(staffMember.id)}
                                                                    className="px-3 py-1 text-xs font-medium text-red-600 hover:text-red-800 hover:bg-red-100 rounded"
                                                                >
                                                                    Remove
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Add More Section */}
                                            <div>
                                                <h3 className="text-sm font-semibold text-gray-900 mb-3">Add Tags or Staff</h3>
                                                
                                                {/* Search Bar */}
                                                <div className="mb-3 relative">
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <FontAwesomeIcon icon={faSearch} className="h-4 w-4 text-gray-400" />
                                                    </div>
                                                    <input
                                                        type="text"
                                                        placeholder="Search tags or staff..."
                                                        value={searchQuery}
                                                        onChange={(e) => setSearchQuery(e.target.value)}
                                                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                                    />
                                                </div>
                                                
                                                {availableTags.length === 0 && availableStaff.length === 0 ? (
                                                    <div className="py-4 text-center text-gray-500 text-sm">
                                                        {searchQuery ? "No matching tags or staff found" : "All tags and staff are already assigned"}
                                                    </div>
                                                ) : (
                                                    <div className="space-y-2">
                                                        {/* Available Tags */}
                                                        {availableTags.map((tag) => {
                                                            const icon = getIconComponent(tag.icon);
                                                            return (
                                                                <div
                                                                    key={`available-tag-${tag.id}`}
                                                                    className="flex items-center justify-between p-3 border rounded-md hover:bg-gray-50"
                                                                >
                                                                    <div className="flex items-center">
                                                                        <div className="h-8 w-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-medium mr-3">
                                                                            {icon ? (
                                                                                <FontAwesomeIcon icon={icon} className="h-4 w-4" />
                                                                            ) : (
                                                                                tag.name.substring(0, 2).toUpperCase()
                                                                            )}
                                                                        </div>
                                                                        <span className="text-sm font-medium text-gray-900">{tag.name}</span>
                                                                        <span className="ml-2 text-xs text-gray-500">(Tag)</span>
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleAddTag(tag.id)}
                                                                        className="px-3 py-1 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded"
                                                                    >
                                                                        Add
                                                                    </button>
                                                                </div>
                                                            );
                                                        })}
                                                        
                                                        {/* Available Staff */}
                                                        {availableStaff.map((staffMember) => (
                                                            <div
                                                                key={`available-staff-${staffMember.id}`}
                                                                className="flex items-center justify-between p-3 border rounded-md hover:bg-gray-50"
                                                            >
                                                                <div className="flex items-center">
                                                                    <div className="h-8 w-8 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-medium mr-3">
                                                                        {staffMember.name.substring(0, 2).toUpperCase()}
                                                                    </div>
                                                                    <span className="text-sm font-medium text-gray-900">{staffMember.name}</span>
                                                                    <span className="ml-2 text-xs text-gray-500">(Staff)</span>
                                                                </div>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleAddStaff(staffMember.id)}
                                                                    className="px-3 py-1 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded"
                                                                >
                                                                    Add
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </>
                                    )}

                                    <div className="mt-6 flex justify-end">
                                        <button
                                            type="button"
                                            onClick={() => dispatch(closeModal())}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                        >
                                            Close
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
